import {
  Checkbox,
  Dialog,
  Heading,
  Input,
  Link,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";

import { Button } from "../components/button";
import { Field } from "../components/field";
import LoginShell from "../components/login-shell";
import { PasswordInput } from "../components/password-input";
import { StyledMarkdown } from "../components/styled-markdown";
import { toaster } from "../components/toaster";
import { useAppDispatch, useAppSelector } from "../hooks";
import { setReturnLocation, signUp } from "../store/authSlice";

interface SignupForm {
  username: string;
  password: string;
  agreeToTerms: boolean;
}

const TERMS_AND_CONDITIONS = `These Terms & Conditions ("Terms") are a binding agreement between Octane Software, Inc. ("Vendor") and the company or organization you represent ("Customer"). By accepting these Terms, you confirm that you have authority to bind Customer and are agreeing on its behalf.

By creating an account or using the platform (including trials, proofs of concept, unpaid access, or paid access), Customer agrees to the following Terms:

## "As-Is" Software

The platform and all related services are provided "as is" without warranties of any kind.

Vendor does not guarantee specific results, improvements, performance levels, financial outcomes, or the behavior of advertising platforms.

Any "guarantees" referenced in marketing materials, sales conversations, or other communications are intended solely to describe the expectations and goals of the Trial or proof of concept.

Such guarantees apply only to the Trial period and serve only to indicate that Customer is free to discontinue use at the end of the Trial if those expectations are not met.

These marketing guarantees do not apply after Customer enters into a paid agreement.

Failure to achieve any stated performance level after the Trial period does not release Customer from payment obligations under any separate paid contract.

## Limitations of Liability

To the fullest extent allowed by law:

- Vendor is not liable for indirect, incidental, or consequential damages of any kind.
- Vendor's total liability for any claim related to the platform or services will not exceed $100.

## Revocation and Termination

Customer may stop using the platform at any time.

Vendor may suspend or terminate access if necessary to protect the service, comply with law, or respond to misuse or inaccessibility.

Any permissions granted by Customer to Vendor (including marketing permissions below) may be revoked at any time by sending written notice via email.

## Use of Results, Name, and Logo

Customer gives Vendor permission to reference Customer's name, logo, and performance results generated through Customer's use of the platform (including trials, before/after comparisons, efficiency metrics, and performance screenshots).

Vendor will not disclose specific budgets, internal documents, or data reasonably considered sensitive.

Customer may revoke this permission at any time by emailing Vendor, and Vendor will stop using Customer's materials and remove them within a reasonable period.

## Dispute Resolution

Any dispute arising out of or relating to the platform or services will be resolved through binding arbitration, not court.

Both parties waive the right to a trial by jury.

Arbitration will be conducted by a neutral arbitrator in Delaware, unless both parties agree otherwise.

## Governing Law

These Terms are governed by the laws of the State of Delaware, without regard to conflict-of-laws rules.

## Updates to These Terms

Vendor may update these Terms periodically. Customer's continued use of the platform after changes are posted means Customer accepts the updated Terms.

## Priority of Signed Agreements

If Customer and Vendor enter into a separate, signed agreement governing the use of the platform or services, payments, or any other subject, that agreement will supersede these Terms to the extent of any conflict.`;

export default function SignUpPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { returnLocation } = useAppSelector((s) => s.auth);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isTermsDialogOpen, setIsTermsDialogOpen] = React.useState(false);
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<SignupForm>();
  const onSubmit = handleSubmit(async ({ username, password }) => {
    setIsLoading(true);
    try {
      await dispatch(signUp({ username, password })).unwrap();
      dispatch(setReturnLocation(null));
      navigate(returnLocation || "/");
    } catch (e) {
      toaster.create({
        type: "error",
        title: "Error",
        description: e.message,
      });
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <LoginShell>
      <Stack gap={{ base: "2", md: "3" }} textAlign="center">
        <Heading color="fg.accent" size={{ base: "2xl", md: "3xl" }}>
          Sign Up
        </Heading>
      </Stack>
      <form onSubmit={onSubmit}>
        <Stack gap="6">
          <Stack gap="5">
            <Field
              label="Email"
              invalid={!!errors.username}
              errorText={errors.username?.message}
            >
              <Input
                type="email"
                placeholder="johndoe@example.com"
                {...register("username", {
                  required: "Please provide an e-mail address.",
                })}
              />
            </Field>
            <Field
              label="Password"
              invalid={!!errors.password}
              errorText={errors.password?.message}
            >
              <PasswordInput
                {...register("password", {
                  required: "Please provide a password.",
                })}
              />
            </Field>
            <Field
              invalid={!!errors.agreeToTerms}
              errorText={errors.agreeToTerms?.message}
            >
              <Controller
                name="agreeToTerms"
                control={control}
                rules={{
                  required:
                    "You must agree to the terms and conditions to sign up.",
                }}
                render={({ field }) => (
                  <Checkbox.Root
                    checked={field.value}
                    onCheckedChange={(e) => field.onChange(!!e.checked)}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>
                      To complete your account, please agree to our{" "}
                      <Link
                        variant="underline"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsTermsDialogOpen(true);
                        }}
                      >
                        terms and conditions
                      </Link>
                    </Checkbox.Label>
                  </Checkbox.Root>
                )}
              />
            </Field>
          </Stack>

          <Dialog.Root
            open={isTermsDialogOpen}
            onOpenChange={(e) => setIsTermsDialogOpen(e.open)}
          >
            <Portal>
              <Dialog.Backdrop />
              <Dialog.Positioner>
                <Dialog.Content maxW="2xl" css={{ maxHeight: "80vh" }}>
                  <Dialog.Header>
                    <Dialog.Title>Terms and Conditions</Dialog.Title>
                  </Dialog.Header>
                  <Dialog.Body overflowY="auto">
                    <StyledMarkdown>{TERMS_AND_CONDITIONS}</StyledMarkdown>
                  </Dialog.Body>
                  <Dialog.Footer>
                    <Button onClick={() => setIsTermsDialogOpen(false)}>
                      Close
                    </Button>
                  </Dialog.Footer>
                </Dialog.Content>
              </Dialog.Positioner>
            </Portal>
          </Dialog.Root>
          <Stack gap="4">
            <Button loading={isLoading} type="submit">
              Sign Up
            </Button>
          </Stack>

          <Text textStyle="sm" color="fg.muted" textAlign="center">
            Already have an account?{" "}
            <Link asChild variant="underline">
              <RouterLink to="/login">Sign In</RouterLink>
            </Link>
          </Text>
        </Stack>
      </form>
    </LoginShell>
  );
}
